import { ChangeDetectionStrategy, Component, Input, TemplateRef, ViewChild } from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatMenu, MatMenuTrigger, MenuPositionX } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs';

import { LangCode } from '../../../models/localization.model';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { UserService } from '../../../services/user.service';
import {
  ColorScheme,
  PencilBottomSheetContainerComponent,
} from '../../../standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';

interface LanguageOption {
  lang: LangCode;
  label: string;
  dataName: string;
}

/**
 * Language list rendered as a menu (bind a trigger to `menu`) or, on small screens, a bottom sheet.
 * Triggers should call `open()` instead of relying on the trigger's own click handling.
 */
@UntilDestroy()
@Component({
  selector: 'app-language-menu',
  templateUrl: './language-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageMenuComponent {
  @Input() dataNamePrefix = '';
  @Input() dataNameField: 'label' | 'originalLabel' = 'label';
  @Input() xPosition: MenuPositionX = 'after';

  @ViewChild('langMenu', { static: true }) menu!: MatMenu;
  @ViewChild('languageBottomSheet')
  languageBottomSheet?: TemplateRef<PencilBottomSheetContainerComponent>;

  languageOptions$ = this.userService.langCodes.pipe(
    map((langs) => langs.map((lang) => this.toOption(lang))),
  );
  selectedLangCode$ = this.userService.selectedLangCode;
  readonly ColorScheme = ColorScheme;
  private renderAsBottomSheet = false;
  private bottomSheetRef?: MatBottomSheetRef;

  constructor(
    private userService: UserService,
    private screenSizeService: ScreenSizeService,
    private bottomSheet: MatBottomSheet,
  ) {
    this.screenSizeService.renderMenuAsBottomSheet$
      .pipe(untilDestroyed(this))
      .subscribe((renderAsBottomSheet) => (this.renderAsBottomSheet = renderAsBottomSheet));
  }

  open(event: Event, trigger: MatMenuTrigger): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.renderAsBottomSheet && this.languageBottomSheet) {
      this.bottomSheetRef = this.bottomSheet.open(this.languageBottomSheet, {
        panelClass: ['pencil-bottom-sheet', 'light-scheme'],
        closeOnNavigation: true,
      });
      return;
    }

    trigger.openMenu();
  }

  closeBottomSheet(): void {
    this.bottomSheetRef?.dismiss();
    this.bottomSheetRef = undefined;
  }

  trackByCode(_: number, option: LanguageOption): string {
    return option.lang.code;
  }

  private toOption(lang: LangCode): LanguageOption {
    return {
      lang,
      label: `${lang.originalLabel} / ${lang.label}`,
      dataName: this.dataNamePrefix + (lang[this.dataNameField] ?? '').toLowerCase(),
    };
  }

  selectLanguage(lang: LangCode): void {
    this.closeBottomSheet();
    this.userService.confirmLanguageChange(this.userService.selectedLangCode.value, lang);
  }
}
